/*
 * Copyright (c) CIRROM 2026.
 */

import React, { useState } from "react";
import { ReviewModalShell } from "./ReviewModalShell";
import { useRecordReview } from "./helpers/useRecordReview";
import { RecordReviewCard } from "./RecordReviewCard";

/*
 * The record-review surface as a modal, standing in for
 * SuggestedChangeDetailsModal behind the combined-record-modal preference.
 *
 * The card was built for this from the start - it takes only data props - so
 * everything here is hosting: the data, the busy guard the shell enforces,
 * and the queue navigation the grid owns.
 *
 * Ids arrive the way the grid has them: a change id and a batch id, and a
 * record id that may or may not be present. The system id is not on the row
 * at all, so useRecordReview derives it from the explain payload before it
 * can ask Monitor anything.
 */
export const RecordReviewModal = (props: {
  open: boolean;
  onClose: () => void;
  suggestedChangeId?: number | null;
  batchId?: number | null;
  recordId?: number | null;
  integratedSystemId?: number | null;
  onPreviousRowRequested?: () => void;
  onNextRowRequested?: () => void;
  /** The suggested-change ids that are no longer pending, for the grid. */
  onSuccess: (decidedChangeIds?: number[]) => void;
}) => {
  const {
    model,
    bothFailed,
    unavailableSources,
    effectiveRecordId,
    effectiveSystemId,
    reload,
    reloadSilently,
  } = useRecordReview(
    {
      integratedSystemId: props.integratedSystemId,
      recordId: props.recordId,
      suggestedChangeId: props.suggestedChangeId,
      batchId: props.batchId,
    },
    // Nothing is fetched for a closed modal; opening it starts the load.
    props.open
  );
  const [busy, setBusy] = useState(false);

  return (
    <ReviewModalShell
      open={props.open}
      onClose={props.onClose}
      busy={busy}
      failed={bothFailed}
      failureMessage="This record could not be loaded. The requests may have failed, in which case trying again will recover it."
      onRetry={reload}
    >
      {model && (
        <RecordReviewCard
          model={model}
          integratedSystemId={effectiveSystemId}
          recordId={effectiveRecordId}
          suggestedChangeId={props.suggestedChangeId}
          unavailableSources={unavailableSources}
          onChanged={(decidedChangeIds) => {
            reloadSilently();
            props.onSuccess(decidedChangeIds);
          }}
          onBusyChange={setBusy}
          onRetry={reload}
          onClose={props.onClose}
          onPreviousRowRequested={props.onPreviousRowRequested}
          onNextRowRequested={props.onNextRowRequested}
        />
      )}
    </ReviewModalShell>
  );
};
